import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Video } from '@fake-stack-overflow/shared';
import useUserContext from '../../../../hooks/useUserContext';
import useVideoVoting from '../../../../hooks/useVideoVoting';
import useWatchParticipants from '../../../../hooks/useWatchParticipants';
import useWatchPartyQueue from '../../../../hooks/useWatchQueue';
import useYouTubeSearch from '../../../../hooks/useYoutubeSearch';
import useSyncPlayback from '../../../../hooks/useSyncPlayback';
import VideoPlayer from '../videoPlayer';
import QueueList from '../queueList';
import ManualVideoForm from '../manualVideoForm';
import YouTubeSearch from '../youtubeSearch';
import VotingSection from '../votingSection';
import ParticipantsList from '../participantsList';
import './index.css';
import { updateCurrentVideo } from '../../../../services/watchService';
import WatchChatPage from '../chat';

const CurrentParty = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { watchPartyId } = useParams();
  const { user, socket } = useUserContext();
  const navigate = useNavigate();

  const { participants, host, handleParticipantLeft } = useWatchParticipants(
    watchPartyId as string,
  );
  const {
    queue,
    currentVideo,
    message,
    manualLink,
    addVideo,
    clearMessage,
    nextVideo,
    handleAddManualVideo,
    setManualLink,
  } = useWatchPartyQueue(watchPartyId as string, host as string);

  const { playerRef, handlePlay, handlePause, handleSeek } = useSyncPlayback(
    watchPartyId as string,
    host as string,
    currentVideo?.url as string,
  );

  const [searchQuery, setSearchQuery] = useState('');
  const { videos, loading, error, searchYouTube, clearSearch } = useYouTubeSearch();

  const {
    activeVote,
    activeReorderVote,
    startVote,
    voteForVideoRemove,
    concludeVote,
    concludeWithNoWinner,
    startReorderQueue,
    voteForReorder,
    concludeReorderQueue,
  } = useVideoVoting(watchPartyId as string, queue);

  const handleLeaveParty = () => {
    handleParticipantLeft({ watchPartyId: watchPartyId as string, user: user.username });
    navigate('/watch');
  };

  const handleEndParty = () => {
    socket.emit('partyEnded', watchPartyId as string);
    handleParticipantLeft({ watchPartyId: watchPartyId as string, user: user.username });
    navigate('/watch');
  };

  const saveVideoToDatabase = async (video: { title: string; url: string }) => {
    try {
      if (video) {
        await updateCurrentVideo(watchPartyId as string, video);
      }
    } catch (e) {
      /* empty */
    }
  };

  const handleNextVideo = () => {
    if (host === user.username && currentVideo) {
      saveVideoToDatabase(currentVideo); // Save the video before moving on
      nextVideo(); // Move to the next video in the queue
    }
  };

  return (
    <div className='watch-party-page'>
      <div className='control-container'>
        {host === user.username && (
          <div className='next-video-container'>
            <button type='button' onClick={handleNextVideo} className='btn-next-video'>
              Next Video
            </button>
          </div>
        )}

        {host === user.username && (
          <div className='party-code'>
            <strong>Party Code:</strong> {watchPartyId}
          </div>
        )}

        {/* leave button label changes depending on if the user is a host or not */}
        <button
          className='btn-leave-party'
          type='button'
          onClick={user.username === host ? handleEndParty : handleLeaveParty}>
          {user.username === host ? 'End Party' : 'Leave Party'}
        </button>
      </div>

      <div>
        <div className='main-container'>
          <div className='video-container'>
            <VideoPlayer
              playerRef={playerRef}
              currentVideo={currentVideo}
              handlePlay={event => {
                handlePlay(event);
                saveVideoToDatabase(currentVideo as Video);
              }}
              handlePause={handlePause}
              handleSeek={handleSeek}
              handleEnd={handleNextVideo}
            />
          </div>
          <div className='video-info-container'>
            <QueueList queue={queue} />
            <ParticipantsList
              participants={participants}
              host={host || ''}
              currentUser={user.username}
              isCurrentUserHost={host === user.username}
            />
          </div>

          <div className={`chat-room-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
            <div className='chat-header' onClick={() => setIsExpanded(!isExpanded)}>
              <h2 className='chat-title'>Chat Room</h2>
              <span className='toggle-icon'>{isExpanded ? '−' : '+'}</span>
            </div>

            {isExpanded && <WatchChatPage id={watchPartyId as string} hideHeader={true} />}
          </div>
        </div>

        <div className='video-controls-container'>
          <VotingSection
            user={user}
            host={host || ''}
            activeVote={activeVote}
            activeReorderVote={activeReorderVote}
            startVote={startVote}
            voteForVideoRemove={voteForVideoRemove}
            concludeVote={concludeVote}
            concludeWithNoWinner={concludeWithNoWinner}
            startReorderQueue={startReorderQueue}
            voteForReorder={voteForReorder}
            concludeReorderQueue={concludeReorderQueue}
            queue={queue}
          />
          <div className='search-container'>
            <ManualVideoForm
              manualLink={manualLink}
              setManualLink={setManualLink}
              handleAddManualVideo={handleAddManualVideo}
              message={message}
            />

            <YouTubeSearch
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchYouTube={searchYouTube}
              clearSearch={clearSearch}
              clearMessage={clearMessage}
              videos={videos}
              loading={loading}
              error={error}
              addVideo={addVideo}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentParty;
